import { veiraCartState, cartSections, applyCartSections, readStableCart, installVeiraGiftCleanup } from './veira-cart-state.js?v=20261027-4';
import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}


let veiraGiftBusy = false;

class VeiraCartGift extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.usedPointer = false;
    this.addEventListener('pointerdown', () => { this.usedPointer = true; });
    this.addEventListener('keydown', () => { this.usedPointer = false; });
    this.addEventListener('change', event => { if (event.target.matches('input[type=checkbox]')) this.updateGift(); });
  }

  async updateGift() {
    const checkbox = this.querySelector('input');
    const status = this.querySelector('[role="status"]');
    if (!checkbox) return;
    if (veiraGiftBusy || window.__veiraGiftMutationBusy) { checkbox.checked = !checkbox.checked; return; }
    window.__veiraGiftMutationBusy = true;
    const restoreKeyboardFocus = !this.usedPointer;
    const desired = checkbox.checked;
    const previous = !desired;
    veiraGiftBusy = true;
    checkbox.disabled = true;
    this.setAttribute('aria-busy', 'true');
    status.textContent = '';
    const drawer = this.closest('#cart-drawer');
    const controls = Array.from(drawer?.querySelectorAll('button, input, select, textarea') || []);
    const disabledStates = controls.map(control => control.disabled);
    controls.forEach(control => { control.disabled = true; });
    const checkoutLinks = Array.from(drawer?.querySelectorAll('a[href*="/checkout"]') || []);
    const blockCheckout = event => event.preventDefault();
    checkoutLinks.forEach(link => link.addEventListener('click', blockCheckout));
    let mutated = false;
    const base = this.dataset.root || '/';
    const endpoint = path => new URL(base + path, location.origin).href;
    const readJSON = async response => {
      const data = await response.json();
      if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : 'Sepet güncellenemedi. Tekrar deneyebilirsin.');
      return data;
    };
    try {
      const liveCart = await readStableCart(base);
      const lines = liveCart.items.filter(item => String(item.product_id) === this.dataset.productId);
      if (desired && !liveCart.items.some(item => String(item.product_id) !== this.dataset.productId && item.quantity > 0)) {
        throw new Error('Hediye paketi eklemek için sepette bir ürün olmalı.');
      }
      const sections = ['cart-drawer-section'];
      let payload;
      let action;
      if (desired && !lines.length) {
        if (this.dataset.available !== 'true') throw new Error('Hediye paketi şu an stokta yok.');
        action = 'cart/add.js';
        payload = {items:[{id:Number(this.dataset.variantId),quantity:1}]};
      } else {
        const updates = {};
        lines.forEach((line,index) => {updates[line.key] = desired && index === 0 ? 1 : 0;});
        action = 'cart/update.js';
        payload = {updates};
      }
      const mutationRevision = veiraCartState.revision;
      payload.sections = sections;
      payload.sections_url = location.pathname + location.search;
      const result = await fetch(endpoint(action), {
        method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json'},body:JSON.stringify(payload)
      }).then(readJSON);
      mutated = true;
      const unchanged = veiraCartState.revision === mutationRevision;
      const completedRevision = veiraCartState.changed();
      await applyCartSections(sections, result.sections, unchanged ? completedRevision : -1);
      const nextGift = document.querySelector('#cart-drawer veira-cart-gift');
      const nextCheckbox = nextGift?.querySelector('input');
      if (nextCheckbox && restoreKeyboardFocus) nextCheckbox.focus({preventScroll:true});
      const nextStatus = nextGift?.querySelector('[role="status"]');
      if (nextStatus) nextStatus.textContent = '';
      window.dispatchEvent(new Event('resize'));
    } catch (error) {
      if (!mutated) checkbox.checked = previous;
      const liveStatus = (mutated && document.querySelector('#cart-drawer veira-cart-gift [role=\"status\"]')) || status;
      console.error('[veira-cart] Sepet görünümü güncellenemedi:', error);
      liveStatus.textContent = mutated ? `Sepet kaydedildi. Görünüm hatası: ${error.message}` : error.message;
    } finally {
      veiraGiftBusy = false;
      window.__veiraGiftMutationBusy = false;
      if (mutated) veiraCartState.changed();
      window.__veiraGiftCleanup?.schedule();
      this.removeAttribute('aria-busy');
      controls.forEach((control,index) => {if(control.isConnected) control.disabled = disabledStates[index];});
      checkoutLinks.forEach(link => link.removeEventListener('click', blockCheckout));
      if(this.isConnected) checkbox.disabled = !checkbox.checked && this.dataset.available !== 'true';
    }
  }
}

if (!customElements.get('veira-cart-gift')) customElements.define('veira-cart-gift', VeiraCartGift);


installVeiraGiftCleanup();
